import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import RadioGroup from '../../common/RadioGroup/RadioGroup';
import Radio from '../../common/Radio';
import styles from './ExportPricingOptionsModal.module.scss';
import DropDownFilter from '../../filters/DropDownFilter';
import { subMonths } from 'date-fns';
import { useMixpanel } from 'react-mixpanel-browser';
import { reportBusinessActionToMixpanel } from '../../../utils/reportBusinessActionToMixpanel';
import { getAvailablePricingReportFilters, getSupplierAvailableCurrencies } from '../../../store/actions/pricing';
import { ReportType } from '../../../constants/exportPricingReportType';

export function ExportPricingOptionsModal(props) {
  const { onExport } = props;
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.pricings);
  const supplier = useSelector((state) => state.role.supplier);

  const [options, setOptions] = useState({
    currentFilters: true,
    reportType: ReportType.ACTIVE,
    expiredPricesPreviousMonths: 1,
  });

  const mixpanel = useMixpanel();
  const reportBi = useCallback(
    (event, meta) => {
      reportBusinessActionToMixpanel(mixpanel, event, meta);
    },
    [mixpanel],
  );

  const setReportType = (value) => {
    updateOption('reportType', value);
  };
  const setExpiredPricesPreviousMonths = (value) => {
    updateOption('expiredPricesPreviousMonths', value);
  };

  const updateOption = (key, value) => {
    setOptions((prevOptions) => ({
      ...prevOptions,
      [key]: value,
    }));
  };
  const onSubmit = () => {
    const { reportType, currentFilters, expiredPricesPreviousMonths } = options;
    const params = {};
    if (reportType === ReportType.ACTIVE_PAST) {
      params.expired_prices_from_date = subMonths(new Date(), expiredPricesPreviousMonths);
    }
    if (reportType === ReportType.ACTIVE_FUTURE) {
      params.include_future_prices = true;
    }

    dispatch(onExport(params, currentFilters));
    const biMeta = {
      supplierId: supplier.id,
      report_type: reportType,
      ...(reportType === ReportType.ACTIVE_PAST && { previous_months: expiredPricesPreviousMonths }),
      ...(currentFilters
        ? getAvailablePricingReportFilters({
            filters,
            currencies: getSupplierAvailableCurrencies(supplier),
          })
        : undefined),
    };
    reportBi('export_pricing_report', biMeta);
    props.onClose();
  };

  return (
    <div className={styles.wrapper}>
      <h5>Export Products pricing (CSV)</h5>
      <fieldset>
        <legend>Data:</legend>
        <RadioGroup
          row
          name="currentFilters"
          selectedvalue={options.currentFilters ? 'yes' : 'no'}
          onChange={(value) => updateOption('currentFilters', value === 'yes')}
        >
          <Radio id="yes" value="yes" label="Current Filters" />
          <Radio id="no" value="no" label="All Products" />
        </RadioGroup>
      </fieldset>
      <fieldset>
        <legend>Report Type:</legend>
        <RadioGroup column name="reportType" selectedvalue={options.reportType} onChange={setReportType}>
          <Radio id={ReportType.ACTIVE} value={ReportType.ACTIVE} label="Active Prices" />
          <Radio id={ReportType.ACTIVE_FUTURE} value={ReportType.ACTIVE_FUTURE} label="Active + Future Prices" />
          <Radio id={ReportType.ACTIVE_PAST} value={ReportType.ACTIVE_PAST} label="Active + Past Prices" />
          {options.reportType === ReportType.ACTIVE_PAST && (
            <DropDownFilter
              onChange={setExpiredPricesPreviousMonths}
              currentIndex={options.expiredPricesPreviousMonths}
              values={[
                {
                  label: 'Previous 1 month',
                  value: 1,
                },
                {
                  label: 'Previous 3 months',
                  value: 3,
                },
                {
                  label: 'Previous 6 months',
                  value: 6,
                },
              ]}
            />
          )}
        </RadioGroup>
      </fieldset>

      <footer>
        <button type="button" className={styles.button} onClick={props.onClose}>
          Close
        </button>

        <button type="button" className={styles.button} onClick={onSubmit}>
          Export
        </button>
      </footer>
    </div>
  );
}

ExportPricingOptionsModal.propTypes = {
  onExport: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};
